import { useState, useEffect } from 'react';
import { parseMarkdownIntoSections } from '../utils/markdownSectionParser';
import useAssistantContent from './useAssistantContent';

/**
 * Custom hook to manage dynamic sections for split view components
 * Combines content extraction and section parsing logic
 */
const useDynamicSections = (conversation, isStreaming, streamingContent, taskId, customParser = null) => {
  const [sections, setSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [visibleSections, setVisibleSections] = useState(10);

  // Extract content using the common hook
  const { content, contentRef, hasContent, isMatchingConversation } = useAssistantContent(
    conversation, 
    isStreaming, 
    streamingContent, 
    taskId
  );

  // Parse content into sections when content changes
  useEffect(() => {
    if (!isMatchingConversation) return;

    // Use custom parser if provided, otherwise use default
    const parser = customParser || parseMarkdownIntoSections;
    const parsedSections = parser(content);
    setSections(parsedSections);
    
    // Initialize expanded state for all sections (default to closed)
    const newExpandedState = {};
    parsedSections.forEach(section => {
      if (expandedSections[section.id] === undefined) {
        newExpandedState[section.id] = false;
      } else {
        newExpandedState[section.id] = expandedSections[section.id];
      }
    });
    setExpandedSections(newExpandedState);
    
  }, [content, isMatchingConversation]);

  // Handler for section expansion/collapse
  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Handler for load more sections
  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 5, sections.length));
  };

  // Handler for show all sections
  const handleShowAll = () => {
    setVisibleSections(sections.length);
  };

  return {
    sections,
    setSections,
    expandedSections,
    setExpandedSections,
    visibleSections,
    setVisibleSections,
    handleSectionToggle,
    handleLoadMore,
    handleShowAll,
    content,
    contentRef,
    hasContent,
    isMatchingConversation
  };
};

export default useDynamicSections;